import React, { useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Trash } from 'phosphor-react';
import {
  CreditApplicationFields,
  CreditApplicationStepField,
  flattenInternalSignatureDocumentFields,
  GroupedCreditApplicationFields,
  InternalSignatureDocumentDefinition,
  InternalSignatureDocumentHouseRepRequirement,
  InternalSignatureDocumentSignerRequirement,
  renderCheckboxOptions,
  renderTemplate,
} from '@dill/dill-shared';
import { DillButton, DillField, DillInput, DillSelect, DillCheckbox } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppButton } from '../../../general/AppButton/AppButton';
import { InternalSignatureDocumentFillableFields } from '../CreditApplicationComponents/InternalSignatureDocumentFillableFields';
import ConditionalSelectedOptionFields from '../CreditApplicationComponents/ConditionalSelectedOptionFields';
import {
  CHECKBOX_STYLE_FIELD_TYPES,
  resolveCheckboxOrientation,
  parseDropdownOptions,
  parseOptionList,
} from '../CreditApplicationComponents/conditionalOptionUtils';
import { isInternalFormFieldVisible } from '../InternalForms/internalFormUtils';
import { isHTML } from '../../../../utils/helpers';
import { COLORS } from '../../../../utils/colors';

// A curated subset of field types relevant to an internal signature document — no credit-app-only types
// (BANK_REFERENCE, TRADE_REFERENCE, DYNAMIC_FORM_DOCUMENT, INVITE_USER, CONDITIONAL_OPTION —
// CONDITIONAL_SELECTED_FIELD is the one CONDITIONAL_* type internal signature documents do support).
// INTERNAL_SIGNER is the one internal-signature-document-specific type: it renders a signer slot rather than a
// fillable field.
const FIELD_TYPE_OPTIONS = [
  { label: 'Text', value: 'TEXT' },
  { label: 'Number', value: 'NUMBER' },
  { label: 'Currency', value: 'CURRENCY' },
  { label: 'Date', value: 'DATE' },
  { label: 'Text Area', value: 'TEXT_AREA' },
  { label: 'Checkbox', value: 'CHECK_BOX' },
  { label: 'Option Field', value: 'OPTION_FIELD' },
  { label: 'State Dropdown', value: 'STATE_DROPDOWN' },
  { label: 'Dropdown', value: 'DROPDOWN' },
  { label: 'Checkbox List', value: 'CHECKBOX_LIST' },
  { label: 'Conditional Selected Field', value: 'CONDITIONAL_SELECTED_FIELD' },
  { label: 'Internal Signer', value: 'INTERNAL_SIGNER' },
];

const SIGNER_SOURCE_OPTIONS = [
  { label: 'Branch Manager', value: 'BRANCH_TM' },
  { label: 'Region Manager', value: 'REGION_RSM' },
  { label: 'Credit App Reviewer', value: 'CREDIT_APP_REVIEWER' },
  { label: 'Credit App Decision Maker', value: 'CREDIT_APP_DECISION_MAKER' },
  { label: 'Sales Rep', value: 'SALES_REP' },
];

const HOUSE_REP_REQUIREMENT_OPTIONS = [
  { label: 'Any sales rep', value: 'ANY' },
  { label: 'House Rep only', value: 'HOUSE_REP_ONLY' },
  { label: 'Non-House Rep only', value: 'NON_HOUSE_REP_ONLY' },
];

const COLUMN_TYPE_OPTIONS = [
  { label: 'One Column', value: 'ONE_COLUMN' },
  { label: 'Two Columns', value: 'TWO_COLUMNS' },
  { label: 'Three Columns', value: 'THREE_COLUMNS' },
];

const columnCountFor = (columnType: string) =>
  columnType === 'THREE_COLUMNS' ? 3 : columnType === 'TWO_COLUMNS' ? 2 : 1;

const gridColsClassFor = (columnType: string) =>
  columnType === 'THREE_COLUMNS'
    ? 'grid-cols-1 md:grid-cols-3'
    : columnType === 'TWO_COLUMNS'
    ? 'grid-cols-1 md:grid-cols-2'
    : 'grid-cols-1';

let internalSignatureDocumentUid = 0;
const nextInternalSignatureDocumentId = () =>
  `internal-signature-document-${Date.now()}-${++internalSignatureDocumentUid}`;
const nextGroupId = () => `field-group-${Date.now()}-${++internalSignatureDocumentUid}`;
const nextFieldId = (name: string) =>
  `${
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'field'
  }-${Date.now()}-${++internalSignatureDocumentUid}`;

// The answers a coversheet field can be shown for ("Show this field only if…"): another field on
// the same coversheet with fixed choices. A checkbox's answer is saved as true.
// The "Always show" choice; the dropdown can't show an empty value as selected.
const ALWAYS_SHOW = 'always-show';

const getVisibilitySourceOptions = (field: CreditApplicationFields) => {
  if (field.fromField === 'CHECK_BOX') return [{ label: 'Checked', value: 'true' }];
  const options =
    field.fromField === 'DROPDOWN'
      ? parseDropdownOptions(field.optionList)
      : ['OPTION_FIELD', 'CONDITIONAL_SELECTED_FIELD'].includes(field.fromField)
      ? parseOptionList(field.optionList)
      : [];
  return options.map((option) => ({ label: option, value: option }));
};

const emptyField = (): CreditApplicationFields => ({
  id: nextFieldId('field'),
  fromField: 'TEXT',
  name: '',
  isRequired: false,
  prefix: '',
  suffix: '',
});

// One row — 1-3 fields side by side per `columnType`. `index`/`groupedPosition` mirror
// GroupedCreditApplicationFieldsSchema's shape (shared with the credit application's own step
// fields) but are unused here: internal signature documents have no repeatable groups, just a fixed list of rows.
const emptyFieldGroup = (): GroupedCreditApplicationFields => ({
  id: nextGroupId(),
  index: 0,
  columnType: 'ONE_COLUMN',
  fields: [emptyField()],
  groupedPosition: 0,
});

const emptySignerRequirement = (): InternalSignatureDocumentSignerRequirement => ({
  role: `signer-${Date.now()}-${++internalSignatureDocumentUid}`,
  roleLabel: '',
  source: 'BRANCH_TM',
  minimumCreditAmount: 0,
  isRequired: true,
});

const emptyInternalSignatureDocument = (): InternalSignatureDocumentDefinition => ({
  id: nextInternalSignatureDocumentId(),
  name: '',
  fieldGroups: [],
  signerRequirements: [],
  gatesReadyForDecision: true,
});

// Live preview of the field list — reuses the exact field-render components DynamicForm.tsx uses
// per fromField type (AppInputField, AppDatePicker, DillCheckbox, RadioInputs), so builders see
// precisely how each field will look when a real user fills it in, matching the same
// rounded-md/GREY_300-BORDER + flex-wrap layout DynamicForm renders fields in. Deliberately excludes
// INTERNAL_SIGNER: there's no fillable signature widget to preview here — same as DynamicForm.tsx
// itself, which has zero signature rendering (that lives one level up, in DynamicStepForm's
// InviteSignature). INTERNAL_SIGNER fields stay configured only via the Signers section below.
const PreviewInternalSignatureDocumentFields = ({
  form,
}: {
  form: InternalSignatureDocumentDefinition;
}) => {
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({});
  const fieldsById = Object.fromEntries(
    form.fieldGroups.flatMap((group) => group.fields).map((field) => [field.id, field])
  );
  const visibleGroups = form.fieldGroups
    .map((group) =>
      group.fields.filter(
        (field) =>
          field.fromField !== 'INTERNAL_SIGNER' &&
          isInternalFormFieldVisible(field, previewValues, fieldsById)
      )
    )
    .filter((fields) => fields.length > 0);
  const fillableFields = visibleGroups.flat();
  if (fillableFields.length === 0) return null;

  return (
    <InternalSignatureDocumentFillableFields
      fields={fillableFields}
      groupSizes={visibleGroups.map((fields) => fields.length)}
      values={previewValues}
      onChange={(fieldId, value) => setPreviewValues((prev) => ({ ...prev, [fieldId]: value }))}
      currencyFormatting
    />
  );
};

// Live preview while building — same idea as PreviewConditionalSelectedField for a
// DYNAMIC_FORM_DOCUMENT field: render `formTemplate` right now using each field's own name as a
// stand-in value (there's no real credit application to pull real fieldValues from yet), via the
// exact same renderTemplate mechanism used at fill/sign time.
const PreviewInternalSignatureDocumentTemplate = ({
  form,
}: {
  form: InternalSignatureDocumentDefinition;
}) => {
  const sampleValues = flattenInternalSignatureDocumentFields(form).reduce<Record<string, string>>(
    (acc, field) => {
      if (field.fromField === 'INTERNAL_SIGNER') return acc;
      if (CHECKBOX_STYLE_FIELD_TYPES.has(field.fromField)) {
        // Same rendering real signed data gets (see InternalSignatureDocumentTemplatePreview.tsx) — the first
        // option stands in as the "selected" one so the preview shows what the checkbox layout
        // will actually look like, not just a plain field name.
        const options = parseOptionList(field.optionList);
        acc[field.id] = renderCheckboxOptions(
          options,
          options[0] ?? '',
          resolveCheckboxOrientation(field),
          field.checkboxPosition ?? 'left'
        );
      } else {
        acc[field.id] = field.name || field.id;
      }
      return acc;
    },
    {}
  );
  const rendered = renderTemplate(form.formTemplate ?? '', sampleValues);
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
      {isHTML(rendered) ? (
        <div dangerouslySetInnerHTML={{ __html: rendered }} />
      ) : (
        <p>{rendered}</p>
      )}
    </div>
  );
};

// Renders as a single-page list of field groups per internal signature document — deliberately no multi-step
// wizard. Embedded directly on the credit application template builder pages
// (AddCreditTemplate.tsx / EditCreditTemplate.tsx) since internal signature documents are defined ON the credit
// application template, not as a separate template entity.
// One step's worth of the template's own credit application fields — the choices for an internal
// signature document's "Visible Credit Application Fields" checklist.
export type CreditApplicationFieldChoiceGroup = {
  stepName: string;
  fields: { id: string; name: string }[];
};

const InternalSignatureDocumentsSection = ({
  internalSignatureDocuments,
  onChange,
  creditApplicationFieldChoices = [],
}: {
  internalSignatureDocuments: InternalSignatureDocumentDefinition[];
  onChange: (forms: InternalSignatureDocumentDefinition[]) => void;
  creditApplicationFieldChoices?: CreditApplicationFieldChoiceGroup[];
}) => {
  const updateForm = (formId: string, patch: Partial<InternalSignatureDocumentDefinition>) =>
    onChange(internalSignatureDocuments.map((f) => (f.id === formId ? { ...f, ...patch } : f)));

  const toggleVisibleField = (
    form: InternalSignatureDocumentDefinition,
    fieldId: string,
    checked: boolean
  ) => {
    const current = form.visibleCreditApplicationFieldIds ?? [];
    updateForm(form.id, {
      visibleCreditApplicationFieldIds: checked
        ? [...current.filter((id) => id !== fieldId), fieldId]
        : current.filter((id) => id !== fieldId),
    });
  };

  const updateFieldGroup = (
    formId: string,
    groupIndex: number,
    patch: Partial<GroupedCreditApplicationFields>
  ) => {
    const form = internalSignatureDocuments.find((f) => f.id === formId);
    if (!form) return;
    updateForm(formId, {
      fieldGroups: form.fieldGroups.map((group, idx) =>
        idx === groupIndex ? { ...group, ...patch } : group
      ),
    });
  };

  // Swaps a row with the one above (-1) or below (+1).
  const moveFieldGroup = (formId: string, groupIndex: number, direction: -1 | 1) => {
    const form = internalSignatureDocuments.find((f) => f.id === formId);
    const target = groupIndex + direction;
    if (!form || target < 0 || target >= form.fieldGroups.length) return;
    const fieldGroups = [...form.fieldGroups];
    [fieldGroups[groupIndex], fieldGroups[target]] = [fieldGroups[target], fieldGroups[groupIndex]];
    updateForm(formId, { fieldGroups });
  };

  // Swaps a field with its neighbour in the same row, left (-1) or right (+1).
  const moveFieldInGroup = (
    formId: string,
    groupIndex: number,
    fieldIndex: number,
    direction: -1 | 1
  ) => {
    const group = internalSignatureDocuments.find((f) => f.id === formId)?.fieldGroups[groupIndex];
    const target = fieldIndex + direction;
    if (!group || target < 0 || target >= group.fields.length) return;
    const fields = [...group.fields];
    [fields[fieldIndex], fields[target]] = [fields[target], fields[fieldIndex]];
    updateFieldGroup(formId, groupIndex, { fields });
  };

  // Keyed by (groupIndex, fieldIndex), not field.id — field.id is itself an editable input here
  // (see "Field Id" below), so looking it up by its own current value would break mid-edit (the id
  // changes out from under the lookup on every keystroke).
  const updateFieldAt = (
    formId: string,
    groupIndex: number,
    fieldIndex: number,
    patch: Partial<CreditApplicationFields>
  ) => {
    const form = internalSignatureDocuments.find((f) => f.id === formId);
    if (!form) return;
    const group = form.fieldGroups[groupIndex];
    if (!group) return;
    updateFieldGroup(formId, groupIndex, {
      fields: group.fields.map((field, idx) =>
        idx === fieldIndex ? { ...field, ...patch } : field
      ),
    });
  };

  // Changing the column count resizes the group's fields to match — padding with blank fields
  // when growing, dropping the trailing ones when shrinking.
  const setGroupColumnType = (formId: string, groupIndex: number, columnType: string) => {
    const form = internalSignatureDocuments.find((f) => f.id === formId);
    if (!form) return;
    const group = form.fieldGroups[groupIndex];
    if (!group) return;
    const targetCount = columnCountFor(columnType);
    const fields = Array.from(
      { length: targetCount },
      (_, idx) => group.fields[idx] ?? emptyField()
    );
    updateFieldGroup(formId, groupIndex, { columnType: columnType as any, fields });
  };

  const updateSignerRequirement = (
    formId: string,
    role: string,
    patch: Partial<InternalSignatureDocumentSignerRequirement>
  ) => {
    const form = internalSignatureDocuments.find((f) => f.id === formId);
    if (!form) return;
    updateForm(formId, {
      signerRequirements: form.signerRequirements.map((req) =>
        req.role === role ? { ...req, ...patch } : req
      ),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Internal Signature Documents</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Internal, supplier-side documents (e.g. a charge sheet) filled and signed by internal
            staff before a submitted application can reach Ready for Decision. Not shown to the
            applicant.
          </p>
        </div>
        <DillButton
          label="Add Internal Signature Document"
          variant="secondary"
          onClick={() =>
            onChange([...internalSignatureDocuments, emptyInternalSignatureDocument()])
          }
        />
      </div>

      {internalSignatureDocuments.map((form) => (
        <div key={form.id} className="rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
          <div className="flex gap-3 items-start">
            <DillField
              label="Form name"
              className="flex-1"
              required
              error={form.name.trim() === '' ? 'Form name is required' : undefined}>
              <DillInput
                value={form.name}
                onChange={(v) => updateForm(form.id, { name: v })}
                placeholder="e.g. Charge Sheet"
              />
            </DillField>
            <div className="flex flex-col items-start gap-1 pt-6">
              <DillCheckbox
                isChecked={form.gatesReadyForDecision !== false}
                onChange={(checked: boolean) =>
                  updateForm(form.id, { gatesReadyForDecision: checked })
                }
              />
              <span className="text-[10px] text-gray-500">Blocks Ready for Decision</span>
            </div>
            <div className="flex flex-col items-start gap-1 pt-6">
              <DillCheckbox
                isChecked={!!form.signInOrder}
                onChange={(checked: boolean) => updateForm(form.id, { signInOrder: checked })}
              />
              <span className="text-[10px] text-gray-500">Sign in order</span>
            </div>
            <DillButton
              label="Delete Form"
              variant="secondary"
              onClick={() => onChange(internalSignatureDocuments.filter((f) => f.id !== form.id))}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-700">
              Visible Credit Application Fields (optional)
            </span>
            <p className="text-[10px] text-gray-400">
              Which parts of the credit application a signer sees on this document&apos;s sign page.
              Leave everything unchecked to show the whole application. Once anything is checked,
              signers see only the checked fields — no bank references or other signatures — and the
              rest never reaches their browser.
            </p>
            {creditApplicationFieldChoices.length > 0 ? (
              <div className="flex flex-col gap-2 max-h-56 overflow-y-auto border border-gray-200 rounded-md p-2">
                {creditApplicationFieldChoices.map((group) => (
                  <div key={group.stepName} className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase">
                      {group.stepName}
                    </span>
                    {group.fields.map((field) => (
                      <div key={field.id} className="flex items-center gap-2">
                        <DillCheckbox
                          isChecked={(form.visibleCreditApplicationFieldIds ?? []).includes(
                            field.id
                          )}
                          onChange={(checked: boolean) =>
                            toggleVisibleField(form, field.id, checked)
                          }
                        />
                        <span className="text-xs text-gray-700">
                          {field.name || field.id}{' '}
                          <code className="text-[10px] text-gray-400">{field.id}</code>
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-gray-400">
                Add fields to this template&apos;s steps to choose from them here.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-700">Source Field Ids (optional)</span>
            <p className="text-[10px] text-gray-400">
              Comma-separated credit application field ids (e.g.{' '}
              <code>customer-or-company-name, amount-of-credit-requested</code>) whose current
              values get pulled onto this internal signature document under the same id — even when
              there&apos;s no matching field below. Use this so the Form Template above can
              reference <code>{'{{that-credit-app-field-id}}'}</code> for context without redefining
              it as a field here.
            </p>
            <AppInputField
              placeholder="e.g. customer-or-company-name, amount-of-credit-requested"
              value={form.sourceFieldIds ?? ''}
              onTextChange={(text: string) => updateForm(form.id, { sourceFieldIds: text })}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-700">Form Template (optional)</span>
            <p className="text-[10px] text-gray-400">
              Behaves the same as a Dynamic Form Document field: write raw HTML with{' '}
              <code>{'{{field-id}}'}</code> placeholders and each field&apos;s value gets plugged in
              by id (see <code>renderTemplate</code>) when the form is shown/signed. Leave blank to
              fall back to a plain field/value list.
            </p>
            <AppInputField
              inputType="textarea"
              value={form.formTemplate ?? ''}
              onTextChange={(text) => updateForm(form.id, { formTemplate: text })}
            />
            {form.formTemplate ? (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold text-gray-500">
                  Preview (using field names as sample values)
                </span>
                <PreviewInternalSignatureDocumentTemplate form={form} />
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-700">Fields</span>
            {form.fieldGroups.map((group, groupIndex) => (
              <div key={group.id} className="w-full rounded GREY_300-BORDER border p-2 my-2">
                <AppDropDown
                  label="Number of Columns"
                  isRequired
                  value={group.columnType}
                  items={COLUMN_TYPE_OPTIONS}
                  onSelectChange={(item: { value: string } | null) =>
                    item && setGroupColumnType(form.id, groupIndex, item.value)
                  }
                />
                <div className={`grid ${gridColsClassFor(group.columnType)} gap-2 mt-2`}>
                  {group.fields.map((field, fieldIndex) => {
                    const isSigner = field.fromField === 'INTERNAL_SIGNER';
                    const hasPrefixSuffix = ['TEXT', 'NUMBER', 'CURRENCY'].includes(
                      field.fromField
                    );
                    return (
                      <div key={fieldIndex} className="min-w-0 rounded GREY_300-BORDER border p-2">
                        {group.fields.length > 1 && (
                          <div className="flex justify-end gap-3 text-xs PRIMARY_500-CLR">
                            {fieldIndex > 0 && (
                              <div
                                className="cursor-pointer flex items-center gap-1"
                                onClick={() =>
                                  moveFieldInGroup(form.id, groupIndex, fieldIndex, -1)
                                }>
                                <ArrowLeft size={14} />
                                Move left
                              </div>
                            )}
                            {fieldIndex < group.fields.length - 1 && (
                              <div
                                className="cursor-pointer flex items-center gap-1"
                                onClick={() =>
                                  moveFieldInGroup(form.id, groupIndex, fieldIndex, 1)
                                }>
                                Move right
                                <ArrowRight size={14} />
                              </div>
                            )}
                          </div>
                        )}
                        <div className="w-full mt-2 mb-4">
                          <AppInputField
                            isRequired
                            placeholder="Enter field name"
                            label="Field Name"
                            value={field.name}
                            onTextChange={(text: string) =>
                              updateFieldAt(form.id, groupIndex, fieldIndex, { name: text })
                            }
                          />
                        </div>
                        <div className="w-full mt-2 mb-4">
                          <AppInputField
                            isRequired
                            placeholder="Enter field id"
                            label="Field Id"
                            value={field.id}
                            onTextChange={(text: string) =>
                              updateFieldAt(form.id, groupIndex, fieldIndex, { id: text })
                            }
                          />
                        </div>
                        <div className="w-full my-2">
                          <AppDropDown
                            label="Field Type"
                            isRequired
                            value={field.fromField}
                            items={FIELD_TYPE_OPTIONS}
                            onSelectChange={(item: { value: string } | null) =>
                              item &&
                              updateFieldAt(form.id, groupIndex, fieldIndex, {
                                fromField: item.value as any,
                                // Conditional Selected Field reads better as a row of options by
                                // default (unlike Option Field/Checkbox List, which default to
                                // column) — only when isRow hasn't already been explicitly set,
                                // so switching types back and forth never clobbers a deliberate
                                // "Are options in a row or column" choice.
                                ...(item.value === 'CONDITIONAL_SELECTED_FIELD' &&
                                field.isRow === undefined
                                  ? { isRow: true }
                                  : {}),
                              })
                            }
                          />
                        </div>
                        {(field.fromField === 'OPTION_FIELD' ||
                          field.fromField === 'CHECKBOX_LIST' ||
                          field.fromField === 'CONDITIONAL_SELECTED_FIELD') && (
                          <div className="my-2">
                            <AppInputField
                              label="Options (comma separated)"
                              value={field.optionList ?? ''}
                              onTextChange={(text: string) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { optionList: text })
                              }
                            />
                          </div>
                        )}
                        {field.fromField === 'DROPDOWN' && (
                          <div className="my-2">
                            <AppInputField
                              label={'Options (separated by "::")'}
                              value={field.optionList ?? ''}
                              onTextChange={(text: string) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { optionList: text })
                              }
                            />
                          </div>
                        )}
                        {(field.id.includes('supplier-branches') ||
                          field.id.includes('supplier-sales-reps')) &&
                          (field.fromField === 'OPTION_FIELD' ||
                            field.fromField === 'CHECKBOX_LIST' ||
                            field.fromField === 'DROPDOWN') && (
                            <div className="my-2 flex flex-col gap-2">
                              <div className="flex flex-wrap gap-2">
                                <div className="flex-1 min-w-[160px]">
                                  <AppDropDown
                                    label="Value to save"
                                    value={field.optionValueSource ?? 'NAME'}
                                    items={[
                                      { label: 'Name', value: 'NAME' },
                                      { label: 'ERP ID', value: 'ERP_ID' },
                                    ]}
                                    onSelectChange={(item) => {
                                      if (item) {
                                        updateFieldAt(form.id, groupIndex, fieldIndex, {
                                          optionValueSource: item.value as 'NAME' | 'ERP_ID',
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                <div className="flex-1 min-w-[160px]">
                                  <AppDropDown
                                    label="Label to show"
                                    value={field.optionLabelSource ?? 'NAME'}
                                    items={[
                                      { label: 'Name', value: 'NAME' },
                                      { label: 'ERP ID', value: 'ERP_ID' },
                                    ]}
                                    onSelectChange={(item) => {
                                      if (item) {
                                        updateFieldAt(form.id, groupIndex, fieldIndex, {
                                          optionLabelSource: item.value as 'NAME' | 'ERP_ID',
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <p className="text-[10px] text-gray-400">
                                ERP ID avoids ambiguity if two branches/reps share a name. Value and
                                label are independent — e.g. save the ERP ID while still showing the
                                name.
                              </p>
                            </div>
                          )}
                        {field.id.includes('supplier-sales-reps') &&
                          (field.fromField === 'OPTION_FIELD' ||
                            field.fromField === 'CHECKBOX_LIST' ||
                            field.fromField === 'DROPDOWN') && (
                            <div className="my-2 flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <DillCheckbox
                                  isChecked={field.houseRepsOnly ?? false}
                                  onChange={(checked: boolean) =>
                                    updateFieldAt(form.id, groupIndex, fieldIndex, {
                                      houseRepsOnly: checked,
                                    })
                                  }
                                />
                                <span
                                  className="text-sm cursor-pointer"
                                  onClick={() =>
                                    updateFieldAt(form.id, groupIndex, fieldIndex, {
                                      houseRepsOnly: !field.houseRepsOnly,
                                    })
                                  }>
                                  Only show House Reps
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <div className="flex-1 min-w-[160px]">
                                  <AppInputField
                                    label="Field label when a House Rep is selected"
                                    value={field.houseRepFieldLabel || ''}
                                    placeholder="e.g. House Account Id"
                                    onTextChange={(text: string) =>
                                      updateFieldAt(form.id, groupIndex, fieldIndex, {
                                        houseRepFieldLabel: text,
                                      })
                                    }
                                  />
                                </div>
                                <div className="flex-1 min-w-[160px]">
                                  <AppInputField
                                    label="Field label otherwise"
                                    value={field.nonHouseRepFieldLabel || ''}
                                    placeholder="e.g. Sales Rep Id"
                                    onTextChange={(text: string) =>
                                      updateFieldAt(form.id, groupIndex, fieldIndex, {
                                        nonHouseRepFieldLabel: text,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <p className="text-[10px] text-gray-400">
                                Leave both blank to keep the Field Name above fixed regardless of
                                which rep is selected.
                              </p>
                            </div>
                          )}
                        {(field.fromField === 'OPTION_FIELD' ||
                          field.fromField === 'CONDITIONAL_SELECTED_FIELD') && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.isRow || false}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { isRow: val })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  isRow: !field.isRow,
                                })
                              }>
                              Are options in a row or column
                            </p>
                          </div>
                        )}
                        {(field.fromField === 'OPTION_FIELD' ||
                          field.fromField === 'CHECKBOX_LIST' ||
                          field.fromField === 'CONDITIONAL_SELECTED_FIELD') && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.checkboxPosition === 'right'}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  checkboxPosition: val ? 'right' : 'left',
                                })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  checkboxPosition:
                                    field.checkboxPosition === 'right' ? 'left' : 'right',
                                })
                              }>
                              Show checkbox on the right (affects the printed/emailed template only)
                            </p>
                          </div>
                        )}
                        {!isSigner && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.isRequired}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { isRequired: val })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  isRequired: !field.isRequired,
                                })
                              }>
                              Is Field Required
                            </p>
                          </div>
                        )}
                        {!isSigner &&
                          (() => {
                            const sourceChoices = form.fieldGroups
                              .flatMap((otherGroup) => otherGroup.fields)
                              .filter(
                                (other) =>
                                  other.id !== field.id &&
                                  getVisibilitySourceOptions(other).length > 0
                              );
                            const condition = field.conditionalVisibility;
                            const sourceField = sourceChoices.find(
                              (other) => other.id === condition?.sourceFieldId
                            );
                            const selectedValues = condition?.sourceOptionValues ?? [];
                            return (
                              <div className="my-2 rounded GREY_300-BORDER border p-2 flex flex-col gap-2">
                                <p className="text-sm">Show this field only if…</p>
                                <AppDropDown
                                  label="Field on this document"
                                  value={condition?.sourceFieldId || ALWAYS_SHOW}
                                  items={[
                                    { label: 'Always show', value: ALWAYS_SHOW },
                                    ...sourceChoices.map((other) => ({
                                      label: other.name || other.id,
                                      value: other.id,
                                    })),
                                  ]}
                                  onSelectChange={(item: { value: string } | null) =>
                                    updateFieldAt(form.id, groupIndex, fieldIndex, {
                                      conditionalVisibility:
                                        item?.value && item.value !== ALWAYS_SHOW
                                          ? { sourceFieldId: item.value, sourceOptionValues: [] }
                                          : null,
                                    })
                                  }
                                />
                                {condition?.sourceFieldId && !sourceField && (
                                  <p className="text-[10px] text-red-500">
                                    &quot;{condition.sourceFieldId}&quot; isn&apos;t a field with
                                    choices on this document, so this field always hides.
                                  </p>
                                )}
                                {sourceField && (
                                  <div className="flex flex-col gap-1">
                                    <span className="text-[10px] text-gray-500">is one of</span>
                                    {getVisibilitySourceOptions(sourceField).map((option) => (
                                      <div key={option.value} className="flex items-center gap-2">
                                        <DillCheckbox
                                          isChecked={selectedValues.includes(option.value)}
                                          onChange={(checked: boolean) =>
                                            updateFieldAt(form.id, groupIndex, fieldIndex, {
                                              conditionalVisibility: {
                                                sourceFieldId: sourceField.id,
                                                sourceOptionValues: checked
                                                  ? [...selectedValues, option.value]
                                                  : selectedValues.filter(
                                                      (value) => value !== option.value
                                                    ),
                                              },
                                            })
                                          }
                                        />
                                        <span className="text-xs text-gray-700">
                                          {option.label}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        {field.fromField === 'CONDITIONAL_SELECTED_FIELD' && (
                          <div className="my-2 flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-700">
                              Fields per option
                            </span>
                            <ConditionalSelectedOptionFields
                              optionList={field.optionList ?? ''}
                              fieldId={field.id}
                              onChangeConditionalField={(data) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  conditionalOptionFields: data?.[field.id] ?? undefined,
                                })
                              }
                              valueConditionalFields={
                                field.conditionalOptionFields
                                  ? {
                                      [field.id]: field.conditionalOptionFields as Record<
                                        string,
                                        CreditApplicationStepField | null
                                      >,
                                    }
                                  : null
                              }
                              filteredStardardFieldIds={[]}
                              currentStepFieldIds={[]}
                            />
                          </div>
                        )}
                        {hasPrefixSuffix && (
                          <>
                            <div className="my-2">
                              <AppInputField
                                label="Prefix"
                                value={field.prefix}
                                onTextChange={(text: string) =>
                                  updateFieldAt(form.id, groupIndex, fieldIndex, { prefix: text })
                                }
                              />
                            </div>
                            <div className="my-2">
                              <AppInputField
                                label="Suffix"
                                value={field.suffix}
                                onTextChange={(text: string) =>
                                  updateFieldAt(form.id, groupIndex, fieldIndex, { suffix: text })
                                }
                              />
                            </div>
                          </>
                        )}
                        <div className="my-2 flex flex-col">
                          <AppInputField
                            label="Linked Field Id"
                            value={field.linkedFieldId ?? ''}
                            onTextChange={(text: string) =>
                              updateFieldAt(form.id, groupIndex, fieldIndex, {
                                linkedFieldId: text,
                              })
                            }
                          />
                        </div>
                        <div className="my-2 flex flex-col">
                          <AppInputField
                            inputType="textarea"
                            label="Label Subtext"
                            placeholder="Enter subtext"
                            value={field.labelSubtext ?? ''}
                            onTextChange={(text: string) =>
                              updateFieldAt(form.id, groupIndex, fieldIndex, {
                                labelSubtext: text || null,
                              })
                            }
                          />
                          <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                            Optional text shown below the label.
                          </p>
                        </div>

                        {field.fromField === 'TEXT' && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.isFieldEmail || false}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  isFieldEmail: val,
                                })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  isFieldEmail: !field.isFieldEmail,
                                })
                              }>
                              Is field an email
                            </p>
                          </div>
                        )}

                        {(field.fromField === 'TEXT' || field.fromField === 'NUMBER') && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.isHashed || false}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { isHashed: val })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  isHashed: !field.isHashed,
                                })
                              }>
                              Is Field Hashed
                            </p>
                          </div>
                        )}

                        {!isSigner && (
                          <div className="my-2 flex items-center">
                            <AppRadioButton
                              isChecked={field.hidden || false}
                              onClick={(val: boolean) =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, { hidden: val })
                              }
                            />
                            <p
                              className="text-sm ml-1 cursor-pointer"
                              onClick={() =>
                                updateFieldAt(form.id, groupIndex, fieldIndex, {
                                  hidden: !field.hidden,
                                })
                              }>
                              Hide field when filling application
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="w-full flex justify-end gap-4 my-2 items-center">
                  {groupIndex > 0 && (
                    <div
                      className="cursor-pointer flex items-center text-sm PRIMARY_500-CLR"
                      onClick={() => moveFieldGroup(form.id, groupIndex, -1)}>
                      <div className="mr-1">Move up</div>
                      <ArrowUp size={16} />
                    </div>
                  )}
                  {groupIndex < form.fieldGroups.length - 1 && (
                    <div
                      className="cursor-pointer flex items-center text-sm PRIMARY_500-CLR"
                      onClick={() => moveFieldGroup(form.id, groupIndex, 1)}>
                      <div className="mr-1">Move down</div>
                      <ArrowDown size={16} />
                    </div>
                  )}
                  <div
                    className="cursor-pointer flex items-center text-red-500"
                    onClick={() =>
                      updateForm(form.id, {
                        fieldGroups: form.fieldGroups.filter((_, idx) => idx !== groupIndex),
                      })
                    }>
                    <div className="text-sm mr-1">Remove</div>
                    <Trash color={COLORS.ERROR_400} size={16} />
                  </div>
                </div>
              </div>
            ))}
            <div className="my-2">
              <AppButton
                text="Add Field"
                onClick={() =>
                  updateForm(form.id, { fieldGroups: [...form.fieldGroups, emptyFieldGroup()] })
                }
                type="PRIMARY"
                buttonStyles={{ width: '100px', height: '35px' }}
              />
            </div>
          </div>

          {flattenInternalSignatureDocumentFields(form).some(
            (field) => field.fromField !== 'INTERNAL_SIGNER'
          ) && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold text-gray-700">Preview</span>
              <p className="text-[10px] text-gray-400">
                Exactly how each field renders when filled in — the same components DynamicForm uses
                at fill/sign time. Internal Signer fields are configured under Signers below
                instead.
              </p>
              <PreviewInternalSignatureDocumentFields form={form} />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-gray-700">Signers</span>
            <p className="text-[10px] text-gray-400">
              Who must sign, and at what requested-credit-amount threshold — or, depending on
              whether the application's assigned sales rep is a House Rep — they're required.
              Resolved automatically from the application's assigned branch — Territory Managers
              come from the branch, RSMs from the branch's region.
            </p>
            {form.signerRequirements.map((req) => (
              <div
                key={req.role}
                className="flex flex-wrap gap-2 items-start bg-gray-50 rounded-md p-2">
                <DillField label="Label" className="flex-1 min-w-32">
                  <DillInput
                    value={req.roleLabel}
                    onChange={(v) => updateSignerRequirement(form.id, req.role, { roleLabel: v })}
                    placeholder="e.g. Credit Manager"
                  />
                </DillField>
                <DillField label="Resolved from" className="flex-1 min-w-40">
                  <DillSelect
                    value={req.source}
                    onChange={(v) =>
                      updateSignerRequirement(form.id, req.role, { source: v as any })
                    }
                    options={SIGNER_SOURCE_OPTIONS}
                  />
                </DillField>
                <DillField label="Required at amount ($)" className="flex-1 min-w-40">
                  <DillInput
                    type="number"
                    value={String(req.minimumCreditAmount ?? 0)}
                    onChange={(v) =>
                      updateSignerRequirement(form.id, req.role, {
                        minimumCreditAmount: parseFloat(v) || 0,
                      })
                    }
                  />
                </DillField>
                <DillField label="Required for" className="flex-1 min-w-40">
                  <DillSelect
                    value={req.houseRepRequirement ?? 'ANY'}
                    onChange={(v) =>
                      updateSignerRequirement(form.id, req.role, {
                        houseRepRequirement: v as InternalSignatureDocumentHouseRepRequirement,
                      })
                    }
                    options={HOUSE_REP_REQUIREMENT_OPTIONS}
                  />
                </DillField>
                {req.houseRepRequirement && req.houseRepRequirement !== 'ANY' && (
                  <DillField label="Sales rep field id(s) to check" className="flex-1 min-w-56">
                    <DillInput
                      value={req.houseRepSourceFieldIds ?? ''}
                      onChange={(v) =>
                        updateSignerRequirement(form.id, req.role, {
                          houseRepSourceFieldIds: v,
                        })
                      }
                      placeholder="e.g. charge-no-supplier-sales-reps"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      Comma-separated field id(s) on this document (e.g. one an earlier signer fills
                      in) to check instead of the application&apos;s assigned sales rep. Falls back
                      to the assigned sales rep if left blank or not yet answered.
                    </p>
                  </DillField>
                )}
                <DillButton
                  label="Remove"
                  variant="secondary"
                  className="mt-5"
                  onClick={() =>
                    updateForm(form.id, {
                      signerRequirements: form.signerRequirements.filter(
                        (r) => r.role !== req.role
                      ),
                    })
                  }
                />
              </div>
            ))}
            <DillButton
              label="Add Signer"
              variant="secondary"
              onClick={() =>
                updateForm(form.id, {
                  signerRequirements: [...form.signerRequirements, emptySignerRequirement()],
                })
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default InternalSignatureDocumentsSection;
